import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MeetingBooksLinkApiService } from '../../services/meeting-books-link-api.service';
import {
  MeetingBookingMemberRequest,
  MeetingBookingMemberResponse,
} from '../../models/meeting.model';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../shared/services/user-directory-api.service';

@Component({
  selector: 'app-booking-members',
  standalone: true,
  imports: [CommonModule, FormsModule, EntitySelectComponent],
  templateUrl: './booking-members.component.html',
})
export class BookingMembersComponent implements OnChanges {
  @Input() bookingLinkId!: number;

  items: MeetingBookingMemberResponse[] = [];
  loading = false;
  saving = false;

  newUserId: number | null = null;
  newWeight = 1;

  /** Nombres de usuario por id, para mostrar "Nombre" en vez de "Usuario #id". */
  userNames: Record<number, string> = {};
  /** Correo de usuario por id, para mostrarlo en la tarjeta del miembro (INC 56). */
  userEmails: Record<number, string> = {};

  constructor(
    private api: MeetingBooksLinkApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private userDirectory: UserDirectoryApiService
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (c['bookingLinkId'] && this.bookingLinkId) this.load();
  }

  load(): void {
    this.loading = true;
    this.api.listMembers(this.bookingLinkId).subscribe({
      next: (data) => {
        this.items = data;
        this.loading = false;
        this.loadUserNames();
      },
      error: () => (this.loading = false),
    });
  }

  /**
   * Resuelve los nombres de los miembros listados contra el directorio de la empresa
   * (GET /api/directory/users, solo exige estar autenticado). Antes usaba
   * /api/auth/users/search, gateado con `users:VIEW`: para un rol sin ese permiso la respuesta
   * era 403 y la lista mostraba «Usuario #id».
   */
  private loadUserNames(): void {
    const ids = this.items.map((item) => item.idUser);
    if (!ids.length) return;
    this.userDirectory
      .fetchUsersByIds(ids)
      .pipe(catchError(() => of([] as DirectoryUser[])))
      .subscribe((users) => {
        const names: Record<number, string> = { ...this.userNames };
        const emails: Record<number, string> = { ...this.userEmails };
        for (const u of users) {
          names[u.id] = directoryUserLabel(u);
          if (u.mail) emails[u.id] = u.mail;
        }
        this.userNames = names;
        this.userEmails = emails;
      });
  }

  // ─── Búsqueda de usuario por nombre/correo (server-side, sobre el directorio) ───
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.userDirectory.searchUsers(term, 20).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );

  userLabel = (u: DirectoryUser): string => directoryUserLabel(u);
  userSublabel = (u: DirectoryUser): string => u.mail ?? '';

  add(): void {
    if (!this.newUserId) return;
    this.saving = true;
    const body: MeetingBookingMemberRequest = {
      idUser: +this.newUserId,
      priorityWeight: +this.newWeight || 1,
      isActive: true,
    };
    this.api.createMember(this.bookingLinkId, body).subscribe({
      next: () => {
        this.saving = false;
        this.newUserId = null;
        this.newWeight = 1;
        this.load();
        this.toast.created('Miembro');
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al agregar miembro' });
      },
    });
  }

  updateWeight(item: MeetingBookingMemberResponse, weight: number): void {
    this.api
      .updateMember(this.bookingLinkId, item.id, {
        idUser: item.idUser,
        priorityWeight: +weight,
        isActive: item.isActive,
      })
      .subscribe({ next: () => this.load() });
  }

  toggle(item: MeetingBookingMemberResponse): void {
    this.api
      .updateMember(this.bookingLinkId, item.id, {
        idUser: item.idUser,
        priorityWeight: item.priorityWeight,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.load() });
  }

  async remove(item: MeetingBookingMemberResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Quitar miembro',
      message: '¿Quitar a este miembro?',
      confirmText: 'Quitar'
    });
    if (!ok) return;
    this.api.deleteMember(this.bookingLinkId, item.id).subscribe({
      next: () => { this.load(); this.toast.deleted('Miembro'); },
    });
  }
}
